import { useState } from 'react'
import clsx from 'clsx'
import { DISCUSSION_LIMITS, type DraftImage } from 'lib/community/discussions-writer'
import PhotoTile from 'components/owner-notes/PhotoTile'
import styles from './Writer.module.css'

interface ImagesBlockProps {
  images: DraftImage[]
  /** The set's caption; with `onCaption` absent (an owner review's photos) no caption field. */
  caption?: string
  /** Local previews (object URLs) for photos picked in this visit, by tile key. */
  previews: Readonly<Record<string, string>>
  /** The tile that is the post's cover (first photo of the first set), if it is in this set. */
  coverKey: string | null
  /** How many more photos this set can take (6 per set, 12 per post; 4 on an owner review). */
  room: number
  /** The line after a pick or a drop that left files out. */
  notice: string | null
  onFiles: (files: File[]) => void
  onRemove: (key: string) => void
  onRetry: (key: string) => void
  onCaption?: (value: string) => void
  /** The file input's accessible name (the add tile's own text otherwise). */
  inputLabel?: string
  /** Small tiles that never outgrow a thumbnail (the owner band's photos, next to a one-sentence
   *  field): the grid caps each tile instead of splitting the width three ways. */
  compact?: boolean
  /** The queued tile's line; `null` for none (the owner band holds picks until its sign-in). */
  queuedLabel?: string | null
}

/**
 * A photo set (design 10b): a dashed drop zone, a 3-column grid, the file picker and drag-and-drop
 * (several at once), progress per tile, ✕ per photo, "Cover" on the post's first photo and an
 * optional caption. The upload itself is the writer's; this only renders and reports.
 */
const ImagesBlock: React.VFC<ImagesBlockProps> = ({
  images,
  caption,
  compact = false,
  queuedLabel,
  previews,
  coverKey,
  room,
  notice,
  onFiles,
  onRemove,
  onRetry,
  onCaption,
  inputLabel,
}) => {
  const [over, setOver] = useState(false)
  return (
    <div
      className={clsx(styles.drop, { [styles.dropOver]: over })}
      onDragOver={e => {
        e.preventDefault()
        setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={e => {
        e.preventDefault()
        setOver(false)
        onFiles(Array.from(e.dataTransfer.files))
      }}
    >
      <div className={clsx(styles.grid, { [styles.gridCompact]: compact })}>
        {images.map(image => {
          // This visit's local preview first (no refetch when the upload lands); a reloaded tile
          // has only DealHunt's URL — a posted photo being edited, its smaller stored size.
          const src = previews[image.key] ?? image.preview_url ?? image.url ?? null
          return (
            <PhotoTile
              key={image.key}
              src={src}
              width={image.width}
              height={image.height}
              status={image.status}
              progress={image.progress}
              error={image.error}
              onRetry={() => onRetry(image.key)}
              onRemove={() => onRemove(image.key)}
              queuedLabel={queuedLabel}
            >
              {image.status === 'done' && image.pending_line && (
                <span className={styles.tilePending}>{image.pending_line}</span>
              )}
              {image.key === coverKey && <span className={styles.cover}>{'Cover'}</span>}
            </PhotoTile>
          )
        })}
        {room > 0 && (
          <label className={styles.addTile}>
            <span className={styles.addPlus} aria-hidden="true">
              {'+'}
            </span>
            {'Add photos'}
            <span className={styles.addSub}>{'or drop them here'}</span>
            <input
              className={styles.srOnly}
              type="file"
              accept="image/*"
              multiple
              aria-label={inputLabel}
              onChange={e => {
                onFiles(Array.from(e.target.files ?? []))
                e.target.value = ''
              }}
            />
          </label>
        )}
      </div>
      {notice && (
        <p className={styles.notice} role="status">
          {notice}
        </p>
      )}
      {onCaption && (
        <input
          className={styles.caption}
          type="text"
          value={caption ?? ''}
          maxLength={DISCUSSION_LIMITS.captionChars}
          placeholder="Caption for this photo set (optional)"
          aria-label="Caption for this photo set"
          onChange={e => onCaption(e.target.value)}
        />
      )}
    </div>
  )
}

export default ImagesBlock
